import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AGENCY } from '@/lib/site';
import {
  LOCKUP_PATH,
  LOCKUP_VIEWBOX,
  MONOGRAM_PATH,
  MONOGRAM_VIEWBOX,
} from '@/lib/logoPaths';

/**
 * Vector monogram. Inherits `currentColor` — no background, no raster edges.
 * Give it a text colour (or `style={{ color }}`) and it adapts to any surface.
 */
export const Monogram = ({ className = 'h-8 w-auto', title }) => (
  <svg
    viewBox={MONOGRAM_VIEWBOX}
    className={className}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    role={title ? 'img' : 'presentation'}
    aria-label={title || undefined}
    aria-hidden={title ? undefined : 'true'}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    <path d={MONOGRAM_PATH} fillRule="evenodd" />
  </svg>
);

/** Full stacked lockup: monogram above the ROMILDA / MARKETING HUB wordmark. */
export const LogoLockup = ({ className = 'h-24 w-auto', title = AGENCY.name }) => (
  <svg
    viewBox={LOCKUP_VIEWBOX}
    className={className}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title}
    focusable="false"
  >
    <title>{title}</title>
    <path d={LOCKUP_PATH} fillRule="evenodd" />
  </svg>
);

/**
 * Brand lockup used across the site.
 * variant="lockup"  -> horizontal: monogram + typeset wordmark (header)
 * variant="stacked" -> full vector logo artwork (footer / brand moments)
 * variant="mark"    -> monogram only
 */
export const Logo = ({
  variant = 'lockup',
  className,
  invert = false,
  asLink = true,
  testId = 'brand-logo-link',
}) => {
  const inkColor = invert ? 'var(--rmh-canvas)' : 'var(--rmh-ink)';
  const subColor = invert ? 'rgba(250,247,242,0.66)' : 'var(--rmh-muted)';

  let content;
  if (variant === 'stacked') {
    content = (
      <LogoLockup
        className="h-[104px] w-auto sm:h-28"
        title={`${AGENCY.name} logo`}
      />
    );
  } else if (variant === 'mark') {
    content = <Monogram className="h-9 w-auto" title={`${AGENCY.name} monogram`} />;
  } else {
    content = (
      <span className="flex items-center gap-3">
        <Monogram className="h-[26px] w-auto sm:h-[30px]" />
        <span
          className="block h-7 w-px shrink-0 sm:h-8"
          style={{
            backgroundColor: invert
              ? 'rgba(250,247,242,0.28)'
              : 'var(--rmh-border-strong)',
          }}
          aria-hidden="true"
        />
        <span className="flex flex-col leading-none">
          <span
            className="font-serif text-[0.98rem] tracking-[0.14em] sm:text-[1.06rem]"
            style={{ color: inkColor }}
          >
            ROMILDA
          </span>
          <span
            className="mt-[4px] text-[0.5rem] font-medium tracking-[0.3em] sm:text-[0.55rem]"
            style={{ color: subColor }}
          >
            MARKETING HUB
          </span>
        </span>
      </span>
    );
  }

  const colorStyle = { color: inkColor };

  if (!asLink) {
    return (
      <span
        className={cn('inline-flex items-center', className)}
        style={colorStyle}
      >
        {content}
      </span>
    );
  }

  return (
    <Link
      to="/"
      aria-label={`${AGENCY.name} — home`}
      data-testid={testId}
      style={colorStyle}
      className={cn(
        'inline-flex items-center transition-opacity duration-300 hover:opacity-75',
        className,
      )}
    >
      {content}
    </Link>
  );
};

export default Logo;
